// src/pages/navbar/Contact.jsx
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 35 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.15 }
  }
};

const inputFocus = {
  rest: { scale: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  focus: {
    scale: 1.02,
    boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.2)",
    transition: { type: "spring", stiffness: 300, damping: 20 }
  }
};

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = (formData.get('name') || '').toString().trim();
    const email = (formData.get('email') || '').toString().trim();
    const message = (formData.get('message') || '').toString().trim();

    if (!name || !email || !message) {
      setFormStatus('error');
      return;
    }

    const subject = encodeURIComponent(`WasteZero Contact: Message from ${name}`);
    const body = encodeURIComponent(`Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`);
    window.location.href = `mailto:vijayshikhareteam@gmail.com?subject=${subject}&body=${body}`;
    setFormStatus('success');
    event.currentTarget.reset();
  };

  return (
    <div className="relative min-h-screen bg-linear-to-b from-slate-50 via-emerald-50/45 to-white pt-16 pb-24 md:pt-24 md:pb-32 overflow-hidden">
      <div className="pointer-events-none absolute -top-14 left-0 h-72 w-72 rounded-full bg-emerald-300/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-teal-300/20 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <p className="mb-4 inline-flex items-center rounded-full border border-emerald-200 bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 shadow-sm">
            Contact Hub
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900">
            Get in{' '}
            <span className="bg-linear-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
              Touch
            </span>
          </h1>
          <p className="mt-5 md:mt-6 text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Questions about recycling in Jalgaon? Partnership ideas? We're just a message away.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="bg-white/85 backdrop-blur-md rounded-2xl md:rounded-3xl p-8 md:p-10 shadow-xl shadow-emerald-100/40 border border-white/70"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Send us a message
            </h2>

            <form className="space-y-7" onSubmit={handleSubmit}>
              <motion.div variants={inputFocus} initial="rest" whileFocus="focus">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 py-3.5 px-5 text-base transition-all duration-200"
                  placeholder="Vijay Patil"
                  required
                />
              </motion.div>

              <motion.div variants={inputFocus} initial="rest" whileFocus="focus">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 py-3.5 px-5 text-base transition-all duration-200"
                  placeholder="vijay@example.com"
                  required
                />
              </motion.div>

              <motion.div variants={inputFocus} initial="rest" whileFocus="focus">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  className="block w-full rounded-xl border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 py-3.5 px-5 text-base transition-all duration-200 resize-y min-h-[120px]"
                  placeholder="Tell us how we can help with your waste management needs in Jalgaon..."
                  required
                />
              </motion.div>

              {formStatus === 'success' && (
                <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                  Your message draft has been opened in your email app. We usually reply within 24 hours.
                </p>
              )}

              {formStatus === 'error' && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  Please fill all fields before sending your message.
                </p>
              )}

              <motion.button
                type="submit"
                className="w-full rounded-xl bg-linear-to-r from-emerald-600 to-teal-500 px-8 py-4 text-lg font-bold text-white shadow-lg hover:shadow-emerald-300/50 hover:brightness-110 transition-all duration-300 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Send Message
                <span className="text-xl">→</span>
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            className="space-y-10 md:space-y-12"
          >
            {/* Jalgaon Support Card */}
            <motion.div
              variants={fadeInUp}
              className="bg-white/85 backdrop-blur-md rounded-2xl md:rounded-3xl p-8 shadow-lg shadow-emerald-100/35 border border-white/70"
            >
              <div className="flex items-center gap-4 mb-5">
                <span className="text-4xl">📞</span>
                <h3 className="text-2xl font-bold text-gray-900">Jalgaon Support</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                We're here Monday–Saturday, 10 AM – 7 PM IST
              </p>
              <div className="mt-5 space-y-3 text-gray-700">
                <p className="flex items-center gap-3">
                  <span className="font-medium">Email:</span>
                  <a href="mailto:vijayshikhareteam@gmail.com" className="text-emerald-700 hover:underline">
                    vijayshikhareteam@gmail.com
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <span className="font-medium">Mobile:</span>
                  <a href="tel:9422737898" className="text-emerald-700 hover:underline">
                    9422737898
                  </a>
                </p>
              </div>
            </motion.div>

            {/* Address Card */}
            <motion.div
              variants={fadeInUp}
              className="bg-white/85 backdrop-blur-md rounded-2xl md:rounded-3xl p-8 shadow-lg shadow-emerald-100/35 border border-white/70"
            >
              <div className="flex items-center gap-4 mb-5">
                <span className="text-4xl">📍</span>
                <h3 className="text-2xl font-bold text-gray-900">Our Location</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                WasteZero Operations<br />
                Near MJ College Campus, Ring Road<br />
                Jalgaon, Maharashtra 425001
              </p>
              <a
                href="https://maps.google.com/?q=WasteZero+MJ+College+Ring+Road+Jalgaon"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-emerald-700 font-medium hover:underline"
              >
                Get Directions →
              </a>
            </motion.div>

            {/* Social & Follow */}
            <motion.div
              variants={fadeInUp}
              className="bg-white/85 backdrop-blur-md rounded-2xl md:rounded-3xl p-8 shadow-lg shadow-emerald-100/35 border border-white/70"
            >
              <div className="flex items-center gap-4 mb-5">
                <span className="text-4xl">🌐</span>
                <h3 className="text-2xl font-bold text-gray-900">Follow & Connect</h3>
              </div>
              <div className="flex flex-wrap gap-6 mt-4">
                <a href="https://instagram.com/shikharecoder" target="_blank" rel="noreferrer" className="text-gray-700 hover:text-emerald-600 transition-colors text-lg font-medium">Instagram</a>
                <a href="https://twitter.com/vijayshikhre" target="_blank" rel="noreferrer" className="text-gray-700 hover:text-emerald-600 transition-colors text-lg font-medium">Twitter/X</a>
                <a href="https://linkedin.com/in/vijayshikhare" target="_blank" rel="noreferrer" className="text-gray-700 hover:text-emerald-600 transition-colors text-lg font-medium">LinkedIn</a>
                <a href="https://github.com/vijayshikhare" target="_blank" rel="noreferrer" className="text-gray-700 hover:text-emerald-600 transition-colors text-lg font-medium">GitHub</a>
              </div>
              <p className="mt-6 text-gray-600 text-sm">
                Stay updated on Jalgaon's recycling initiatives and rewards
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Final Note */}
        <motion.div
          className="mt-16 md:mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <p className="text-lg md:text-xl text-gray-700 font-medium">
            Expect a reply within 24 hours on business days. Thank you for helping make Jalgaon cleaner!
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-xl bg-linear-to-r from-emerald-600 to-teal-500 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:brightness-110"
            >
              Join WasteZero
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
            >
              Log In
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}